import { useMemo, useState } from "react";

export interface ResourceScheduleInput {
  id?: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  timezone?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}

export interface ResourceBlockedTimeInput {
  id?: string;
  startAt: string;
  endAt: string;
  reason: "time_off" | "holiday" | "maintenance" | "training" | "personal" | "other";
  notes?: string | null;
}

interface AvailabilityEditorProps {
  schedules: ResourceScheduleInput[];
  blockedTimes: ResourceBlockedTimeInput[];
  onSchedulesChange(next: ResourceScheduleInput[]): void;
  onBlockedTimesChange(next: ResourceBlockedTimeInput[]): void;
}

const DAY_COLUMNS = [
  { dayOfWeek: 1, label: "Mon" },
  { dayOfWeek: 2, label: "Tue" },
  { dayOfWeek: 3, label: "Wed" },
  { dayOfWeek: 4, label: "Thu" },
  { dayOfWeek: 5, label: "Fri" },
  { dayOfWeek: 6, label: "Sat" },
  { dayOfWeek: 0, label: "Sun" },
] as const;

const SLOT_MINUTES = 30;
const SLOT_HOURS = Array.from({ length: 28 }, (_, index) => {
  const totalMinutes = (8 * 60) + (index * SLOT_MINUTES);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
});

const BLOCK_REASON_OPTIONS: Array<ResourceBlockedTimeInput["reason"]> = [
  "time_off",
  "holiday",
  "maintenance",
  "training",
  "personal",
  "other",
];

function timeToMinutes(value: string): number {
  const [hours = 0, minutes = 0] = value.split(":").map(Number);
  return (hours * 60) + minutes;
}

function minutesToTime(value: number): string {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function buildSelectedSlots(schedules: ResourceScheduleInput[]): Set<string> {
  const selected = new Set<string>();
  for (const schedule of schedules) {
    const start = timeToMinutes(schedule.startTime);
    const end = timeToMinutes(schedule.endTime);
    for (let cursor = start; cursor < end; cursor += SLOT_MINUTES) {
      selected.add(`${schedule.dayOfWeek}:${minutesToTime(cursor)}`);
    }
  }
  return selected;
}

function collapseSchedules(selected: Set<string>): ResourceScheduleInput[] {
  const next: ResourceScheduleInput[] = [];

  for (const { dayOfWeek } of DAY_COLUMNS) {
    const dayMinutes = SLOT_HOURS
      .map((slot) => ({ slot, selected: selected.has(`${dayOfWeek}:${slot}`) }))
      .filter((entry) => entry.selected)
      .map((entry) => timeToMinutes(entry.slot))
      .sort((a, b) => a - b);

    if (dayMinutes.length === 0) {
      continue;
    }

    const firstMinute = dayMinutes[0];
    if (typeof firstMinute !== "number") {
      continue;
    }

    let rangeStart = firstMinute;
    let previous = firstMinute;

    for (let index = 1; index <= dayMinutes.length; index += 1) {
      const current = dayMinutes[index];
      if (current === previous + SLOT_MINUTES) {
        previous = current;
        continue;
      }

      next.push({
        dayOfWeek,
        startTime: minutesToTime(rangeStart),
        endTime: minutesToTime(previous + SLOT_MINUTES),
        timezone: "UTC",
      });

      if (typeof current === "number") {
        rangeStart = current;
        previous = current;
      }
    }
  }

  return next;
}

function startOfCurrentWeek(): Date {
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  const day = base.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  base.setDate(base.getDate() + diffToMonday);
  return base;
}

function formatBlockLabel(block: ResourceBlockedTimeInput): string {
  const start = new Date(block.startAt);
  const end = new Date(block.endAt);
  return `${start.toLocaleDateString()} ${start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${end.toLocaleDateString()} ${end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export function AvailabilityEditor({
  schedules,
  blockedTimes,
  onSchedulesChange,
  onBlockedTimesChange,
}: AvailabilityEditorProps) {
  const [pendingBlock, setPendingBlock] = useState<ResourceBlockedTimeInput>({
    startAt: "",
    endAt: "",
    reason: "time_off",
    notes: "",
  });

  const selected = useMemo(() => buildSelectedSlots(schedules), [schedules]);
  const previewWeekStart = useMemo(() => startOfCurrentWeek(), []);

  const toggleSlot = (dayOfWeek: number, slot: string) => {
    const next = new Set(selected);
    const key = `${dayOfWeek}:${slot}`;
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onSchedulesChange(collapseSchedules(next));
  };

  const isBlockedSlot = (dayOfWeek: number, slot: string) => {
    const date = new Date(previewWeekStart);
    const offset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    date.setDate(previewWeekStart.getDate() + offset);
    const [hours = 0, minutes = 0] = slot.split(":").map(Number);
    date.setHours(hours, minutes, 0, 0);
    const slotStart = date.getTime();
    const slotEnd = slotStart + (SLOT_MINUTES * 60 * 1000);

    return blockedTimes.some((block) => {
      const start = new Date(block.startAt).getTime();
      const end = new Date(block.endAt).getTime();
      return start < slotEnd && end > slotStart;
    });
  };

  const addBlockedTime = () => {
    if (!pendingBlock.startAt || !pendingBlock.endAt || pendingBlock.startAt >= pendingBlock.endAt) {
      return;
    }

    onBlockedTimesChange([
      ...blockedTimes,
      {
        ...pendingBlock,
        notes: pendingBlock.notes?.trim() || null,
      },
    ]);

    setPendingBlock({
      startAt: "",
      endAt: "",
      reason: "time_off",
      notes: "",
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Weekly availability</h3>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Toggle 30-minute cells to define the recurring schedule. Green means bookable, gray means unavailable, and red indicates a dated block in the current-week preview.
        </p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <div className="min-w-[860px]">
          <div className="grid border-b border-border bg-muted/20" style={{ gridTemplateColumns: "88px repeat(7, minmax(0, 1fr))" }}>
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Time</div>
            {DAY_COLUMNS.map((day) => (
              <div key={day.dayOfWeek} className="border-l border-border px-3 py-2 text-center text-xs font-semibold text-foreground">
                {day.label}
              </div>
            ))}
          </div>

          {SLOT_HOURS.map((slot) => (
            <div key={slot} className="grid border-b border-border/60 last:border-b-0" style={{ gridTemplateColumns: "88px repeat(7, minmax(0, 1fr))" }}>
              <div className="px-3 py-2 text-[11px] text-muted-foreground">{slot}</div>
              {DAY_COLUMNS.map((day) => {
                const selectedKey = `${day.dayOfWeek}:${slot}`;
                const isSelected = selected.has(selectedKey);
                const isBlocked = isBlockedSlot(day.dayOfWeek, slot);

                return (
                  <button
                    key={selectedKey}
                    type="button"
                    aria-label={`${day.label} ${slot}`}
                    onClick={() => toggleSlot(day.dayOfWeek, slot)}
                    className={`border-l border-border/60 px-1 py-2 transition-colors ${
                      isBlocked
                        ? "bg-[var(--color-error)]/15 hover:bg-[var(--color-error)]/20"
                        : isSelected
                          ? "bg-[var(--color-success)]/15 hover:bg-[var(--color-success)]/25"
                          : "bg-muted/20 hover:bg-muted/35"
                    }`}
                  >
                    <span className="sr-only">{isBlocked ? "Blocked" : isSelected ? "Available" : "Unavailable"}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Blocked time ranges</h3>
            <p className="mt-1 text-xs text-muted-foreground">Use dated blocks for holidays, maintenance, training, and time off.</p>
          </div>
          <span className="rounded-full bg-[var(--color-error)]/10 px-2 py-1 text-[11px] font-medium text-[var(--color-error)]">
            {blockedTimes.length} active
          </span>
        </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>Start</span>
            <input
              type="datetime-local"
              value={pendingBlock.startAt}
              onChange={(event) => setPendingBlock((current) => ({ ...current, startAt: event.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>End</span>
            <input
              type="datetime-local"
              value={pendingBlock.endAt}
              onChange={(event) => setPendingBlock((current) => ({ ...current, endAt: event.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>Reason</span>
            <select
              value={pendingBlock.reason}
              onChange={(event) => setPendingBlock((current) => ({ ...current, reason: event.target.value as ResourceBlockedTimeInput["reason"] }))}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            >
              {BLOCK_REASON_OPTIONS.map((reason) => (
                <option key={reason} value={reason}>{reason.replaceAll("_", " ")}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>Notes</span>
            <input
              value={pendingBlock.notes ?? ""}
              onChange={(event) => setPendingBlock((current) => ({ ...current, notes: event.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              placeholder="Optional note"
            />
          </label>
        </div>

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={addBlockedTime}
            className="rounded-md border border-[var(--color-error)]/30 bg-[var(--color-error)]/10 px-3 py-2 text-sm font-medium text-[var(--color-error)] hover:bg-[var(--color-error)]/15"
          >
            Add Block
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {blockedTimes.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
              No dated blocks configured yet.
            </div>
          ) : blockedTimes.map((block, index) => (
            <div key={`${block.startAt}-${block.endAt}-${index}`} className="flex items-start justify-between gap-3 rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-3 py-3">
              <div className="space-y-1">
                <div className="text-sm font-medium capitalize text-foreground">{block.reason.replaceAll("_", " ")}</div>
                <div className="text-xs text-muted-foreground">{formatBlockLabel(block)}</div>
                {block.notes ? <div className="text-xs text-muted-foreground">{block.notes}</div> : null}
              </div>
              <button
                type="button"
                onClick={() => onBlockedTimesChange(blockedTimes.filter((_, blockIndex) => blockIndex !== index))}
                className="text-sm text-[var(--color-error)] hover:opacity-80"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
